import type { ReactNode } from "react";
import { Button } from "./primitives";

/**
 * Empty, loading and error patterns.
 *
 * Empty is not a failure state and is never drawn as one — it says what would
 * appear here and offers the action that fills it.
 */

export function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon?: string;
  title: string;
  body?: string;
  action?: { label: string; onPress: () => void };
}) {
  return (
    <div className="state-block">
      {icon ? (
        <i
          className={`fa-solid ${icon}`}
          style={{ fontSize: 26, color: "var(--well-text)" }}
          aria-hidden="true"
        />
      ) : null}
      <div className="state-block__title">{title}</div>
      {body ? <div className="state-block__body">{body}</div> : null}
      {action ? (
        <div style={{ marginTop: "var(--space-2)" }}>
          <Button kind="secondary" onClick={action.onPress}>
            {action.label}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Errors name what failed and offer the retry. They never blame the network in
 * the abstract — a student reading "something went wrong" learns nothing.
 */
export function ErrorState({
  title = "That didn’t load",
  body,
  onRetry,
}: {
  title?: string;
  body?: string;
  onRetry?: () => void;
}) {
  return (
    <div className="state-block">
      <i
        className="fa-solid fa-triangle-exclamation"
        style={{ fontSize: 24, color: "var(--warning)" }}
        aria-hidden="true"
      />
      <div className="state-block__title">{title}</div>
      {body ? <div className="state-block__body">{body}</div> : null}
      {onRetry ? (
        <div style={{ marginTop: "var(--space-2)" }}>
          <Button kind="secondary" onClick={onRetry}>
            Try again
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** A shimmering block sized to whatever it stands in for. */
export function Skeleton({
  height,
  width = "100%",
  radius,
}: {
  height: number | string;
  width?: number | string;
  radius?: number;
}) {
  return (
    <div
      className="skeleton"
      style={{ height, width, borderRadius: radius ?? "var(--radius-slip)" }}
    />
  );
}

/**
 * The card skeleton doubles as the loading state, per the design — same
 * geometry, shimmer applied. Using a different shape while loading is what
 * causes the layout to jump when content arrives.
 */
export function CardSkeleton({ media = true }: { media?: boolean }) {
  return (
    <div className="card">
      {media ? (
        <div className="media-well media-well--feed">
          <div className="media-well__shimmer" />
        </div>
      ) : null}
      <div className="card__body">
        <div className="card__attribution">
          <Skeleton height={30} width={30} radius={999} />
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 5 }}>
            <Skeleton height={11} width="42%" radius={4} />
            <Skeleton height={9} width="28%" radius={4} />
          </div>
        </div>
        <Skeleton height={14} width="80%" radius={4} />
        <Skeleton height={12} width="60%" radius={4} />
      </div>
    </div>
  );
}

export function FeedSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
      {Array.from({ length: count }, (_, i) => (
        <CardSkeleton key={i} />
      ))}
    </div>
  );
}

/** Dashed well — an empty slot inside a populated screen (rules D6/D7). */
export function EmptyWell({ children }: { children: ReactNode }) {
  return <div className="well-empty">{children}</div>;
}
